import { useEffect, useRef, useState } from 'react';
import {
  getThemedChat,
  getTalkInstances,
  InstanceID,
  selectConversation,
  User,
} from './helpers';

const getScrollableAncestors = (el: Element): Element[] => {
  const ancestors: Element[] = [];
  let current: Element | null = el.parentElement;
  while (current) {
    const { overflow, overflowY, overflowX } = getComputedStyle(current);
    if (/(auto|scroll)/.test(overflow + overflowY + overflowX)) {
      ancestors.push(current);
    }
    current = current.parentElement;
  }
  return ancestors;
};

const useTalkJS = ({
  theme,
  user,
  instanceID,
  conversationId,
  pendingMessage,
  onMessageSent,
}: {
  theme: string;
  user: User;
  instanceID: InstanceID;
  conversationId?: string | null;
  pendingMessage?: string | null;
  onMessageSent?: () => void;
}) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const onMessageSentRef = useRef(onMessageSent);
  onMessageSentRef.current = onMessageSent;
  const pendingMessageRef = useRef(pendingMessage);
  pendingMessageRef.current = pendingMessage;

  // Latest conversationId for the initial setup call, without forcing the
  // lifecycle effect to re-run on conversation changes. Breaking this into two
  // effects allows for faster conversation switching without the flash of the
  // ghost container.
  const initialConversationIdRef = useRef(conversationId);
  initialConversationIdRef.current = conversationId;

  // Effect 1: chatbox lifecycle. Runs on mount and when the identity of the
  // chatbox changes (theme, user, instanceID). Attaches observers and hides
  // the ghost container on true unmount.
  useEffect(() => {
    let activeContainer: HTMLDivElement | null = null;

    const alignGhost = () => {
      if (!shellRef.current || !activeContainer) return;
      const rect = shellRef.current.getBoundingClientRect();

      const absoluteTop = rect.top + window.scrollY;
      const absoluteLeft = rect.left + window.scrollX;

      Object.assign(activeContainer.style, {
        top: `${absoluteTop}px`,
        left: `${absoluteLeft}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        visibility: rect.height > 0 ? 'visible' : 'hidden',
        pointerEvents: rect.height > 0 ? 'auto' : 'none',
      });
    };

    const setup = async () => {
      const { container } = await getThemedChat({
        theme,
        user,
        instanceID,
        conversationId: initialConversationIdRef.current,
      });

      activeContainer = container;
      alignGhost();
      setIsLoaded(true);
    };

    setup();

    const resizer = new ResizeObserver(alignGhost);
    if (shellRef.current) resizer.observe(shellRef.current);

    window.addEventListener('scroll', alignGhost, { passive: true });
    window.addEventListener('resize', alignGhost);

    const bodyResizer = new ResizeObserver(alignGhost);
    bodyResizer.observe(document.body);

    const scrollableAncestors = shellRef.current
      ? getScrollableAncestors(shellRef.current)
      : [];
    scrollableAncestors.forEach(el =>
      el.addEventListener('scroll', alignGhost, { passive: true }),
    );

    return () => {
      resizer.disconnect();
      bodyResizer.disconnect();
      window.removeEventListener('scroll', alignGhost);
      window.removeEventListener('resize', alignGhost);
      scrollableAncestors.forEach(el =>
        el.removeEventListener('scroll', alignGhost),
      );
      if (activeContainer) {
        activeContainer.style.visibility = 'hidden';
        activeContainer.style.pointerEvents = 'none';
        activeContainer.style.top = '-10000px';
      }
      // Deselect the conversation on the cached Chatbox instance. The ghost
      // container pattern keeps the Chatbox alive across React unmounts for
      // fast re-mount, but TalkJS treats an alive Chatbox with a selected
      // conversation as "being viewed" and auto-marks arriving messages as
      // read. Without this, background messages land silently and
      // `session.unreads.onChange` never fires for them.
      const { chat } = getTalkInstances(theme, instanceID);
      if (chat) chat.select(null);
      setIsLoaded(false);
    };
  }, [theme, user, instanceID]);

  // Effect 2: conversation selection. Decoupled from the lifecycle so
  // switching conversations does not hide the ghost container — TalkJS
  // gates its own iframe contents during chat.select().
  useEffect(() => {
    if (!isLoaded || !conversationId) return;
    const { chat, session } = getTalkInstances(theme, instanceID);
    if (!chat || !session) return;
    const pending = pendingMessageRef.current;
    selectConversation(chat, session, user, conversationId, pending).then(
      () => {
        if (pending) onMessageSentRef.current?.();
      },
    );
  }, [isLoaded, conversationId, theme, instanceID, user]);

  return { shellRef, isLoaded };
};

export default useTalkJS;
